import React, { useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import KPICard from '../../components/primitives/KPICard';
import Panel from '../../components/primitives/Panel';
import StatusPill from '../../components/primitives/StatusPill';
import { PGBarChart, PGPieChart } from '../../components/primitives/PGChart';
import { PG } from '../../utils/theme';
import { getBranch, BRANCHES } from '../../utils/orgData';
import {
  getStaffByBranch,
  getStaff,
  branchMeasure,
  BRANCH_MEASURE,
  BADGES,
  ACHIEVEMENT_FEED,
} from '../../utils/measurabilityData';

const {
  FiTarget,
  FiCrosshair,
  FiClock,
  FiFileText,
  FiAward,
  FiUsers,
  FiZap,
  FiChevronLeft,
  FiTrendingUp,
  FiTrendingDown,
  FiMinus,
} = FiIcons;

const scoreAccent = (s) => (s >= 90 ? PG.emerald : s >= 80 ? PG.amber : PG.red);
const scoreTone = (s) => (s >= 90 ? 'ok' : s >= 80 ? 'warn' : 'alert');
const barColor = (v) => (v >= 95 ? PG.emerald : v >= 85 ? PG.amber : PG.red);

const formatAgo = (m) => {
  if (m < 60) return `${m}M AGO`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem === 0 ? `${h}H AGO` : `${h}H ${rem}M`;
};

const DeltaIcon = ({ delta }) => {
  if (delta > 0) return <FiTrendingUp className="text-[#00C27A]" />;
  if (delta < 0) return <FiTrendingDown className="text-[#D71921]" />;
  return <FiMinus className="text-neutral-500" />;
};

const KPI_ROWS = [
  { key: 'targetPct', label: 'TARGET' },
  { key: 'slaPct', label: 'SLA' },
  { key: 'reportPct', label: 'REPORTS' },
  { key: 'ftrPct', label: 'FTR' },
  { key: 'csat', label: 'CSAT' },
  { key: 'safety', label: 'SAFETY' },
  { key: 'attendance', label: 'ATTEND' },
];

export default function MeasurabilityBranchView() {
  const { branchId } = useParams();
  const branch = getBranch(branchId);
  const measure = branchMeasure(branchId);
  const staff = getStaffByBranch(branchId);

  const [selectedId, setSelectedId] = useState(measure?.topPersonId ?? null);
  const selected = getStaff(selectedId) || staff[0];

  const countryRank = useMemo(() => {
    if (!branch) return '—';
    const peersInCountry = BRANCHES
      .filter((b) => b.country === branch.country)
      .map((b) => BRANCH_MEASURE[b.id])
      .filter(Boolean)
      .sort((a, b) => b.score - a.score);
    const idx = peersInCountry.findIndex((m) => m.id === branchId);
    return idx === -1 ? '—' : idx + 1;
  }, [branchId, branch?.country]);

  const kpiAverages = useMemo(() => {
    if (staff.length === 0) return KPI_ROWS.map(() => 0);
    return KPI_ROWS.map(({ key }) => {
      const sum = staff.reduce((acc, p) => acc + p.kpis[key], 0);
      return +(sum / staff.length).toFixed(1);
    });
  }, [staff]);

  const badgeDistribution = useMemo(() => {
    const counts = {};
    staff.forEach((p) => {
      p.badges.forEach((bid) => {
        counts[bid] = (counts[bid] || 0) + 1;
      });
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([id, value]) => ({ name: BADGES[id].name, value, color: BADGES[id].color }));
  }, [staff]);

  const peerRows = useMemo(() => {
    if (!branch) return [];
    return BRANCHES
      .filter((b) => b.peerGroup === branch.peerGroup)
      .map((b) => BRANCH_MEASURE[b.id])
      .filter(Boolean)
      .sort((a, b) => b.score - a.score);
  }, [branch?.peerGroup]);

  const branchFeed = useMemo(() => {
    if (!branch) return [];
    const matching = ACHIEVEMENT_FEED.filter((e) => {
      const p = getStaff(e.personId);
      return p && p.branchId === branchId;
    });
    const out = matching.slice(0, 15);
    let pad = 0;
    while (out.length < 15) {
      out.push({
        id: `pad-${pad}`,
        personId: null,
        portraitUrl: null,
        text: `${branch.name} on-target streak continues`,
        badgeColor: PG.amber,
        minutesAgo: 180 + pad * 30,
      });
      pad++;
    }
    return out;
  }, [branchId, branch?.name]);

  if (!branch) {
    return <div className="h-full flex items-center justify-center text-neutral-500 font-mono-tech text-xs uppercase tracking-widest">Branch not found</div>;
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="h-full flex flex-col gap-4 min-h-0"
    >
      <div className="flex items-baseline justify-between flex-shrink-0">
        <div>
          <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-neutral-500">
            Measurability / {branch.country} &rsaquo; {branch.region} &rsaquo; {branch.town}
          </div>
          <h1 className="font-doto text-3xl text-white mt-1">{branch.name}</h1>
          <div className="font-mono-tech text-[10px] uppercase tracking-widest text-neutral-500 mt-1">
            Peer: {branch.peerGroup} &middot; {measure.headcount} people
          </div>
        </div>
        <div className="flex items-center gap-3">
          <StatusPill tone={scoreTone(measure.score)}>
            Branch #{countryRank} in {branch.country}
          </StatusPill>
          <Link
            to="/sliver/measurability"
            className="flex items-center gap-1 px-3 py-1.5 border border-white/10 font-mono-tech text-[10px] uppercase tracking-widest text-neutral-500 hover:text-white hover:border-white/30 transition-colors"
          >
            <FiChevronLeft />
            All slivers &middot; Group view
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-5 gap-3 flex-shrink-0">
        <KPICard
          title="BRANCH SCORE"
          value={measure.score}
          unit="/100"
          icon={FiTarget}
          accent={scoreAccent(measure.score)}
          target=">85"
          animated="ticker"
          numericValue={Math.round(measure.score * 10)}
          tickerDigits={4}
          tickerSuffix="/1000"
        />
        <KPICard title="ON TARGET" value={measure.onTargetPct} unit="%" icon={FiCrosshair} accent={PG.amber} target=">70%" />
        <KPICard title="SLA AVG" value={measure.slaAvg} unit="%" icon={FiClock} accent={PG.cyan} target=">95%" />
        <KPICard title="HEADCOUNT" value={measure.headcount} icon={FiUsers} accent={PG.emerald} />
        <KPICard title="BADGES EARNED" value={measure.badgeCount} icon={FiAward} accent={PG.amber} />
      </div>

      <div className="grid grid-cols-12 grid-rows-2 gap-4 flex-1 min-h-0">
        <Panel title="BRANCH_ROSTER" subtitle={`${staff.length} people · click to spotlight`} className="col-span-4">
          <div className="h-full overflow-y-auto custom-scrollbar">
            {staff.map((p) => {
              const isSel = p.id === selected.id;
              const edge = scoreAccent(p.score);
              return (
                <button
                  key={p.id}
                  onClick={() => setSelectedId(p.id)}
                  className={`w-full flex items-center gap-3 p-2 border-l-2 transition-colors ${
                    isSel ? 'bg-[#D71921]/10' : 'hover:bg-white/5'
                  }`}
                  style={{ borderLeftColor: edge }}
                >
                  <img
                    src={p.portraitUrl}
                    alt={p.name}
                    loading="lazy"
                    referrerPolicy="no-referrer"
                    className="w-10 h-10 rounded-full object-cover hover:ring-2 hover:ring-[#D71921] flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0 text-left">
                    <div className="text-sm text-white truncate">{p.name}</div>
                    <div className="font-mono-tech text-[10px] uppercase tracking-widest text-neutral-500 truncate">{p.role}</div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <div className="font-doto text-xl text-white leading-none">{p.score}</div>
                    <DeltaIcon delta={p.deltaWeek} />
                  </div>
                </button>
              );
            })}
          </div>
        </Panel>

        <Panel
          title="SPOTLIGHT"
          subtitle={`rank #${selected.rankNational} national · #${selected.rankRegion} regional · #${selected.rankBranch} branch`}
          className="col-span-5"
          livePulse
        >
          <div className="p-4 flex gap-4 h-full">
            <div className="flex-shrink-0 w-[200px] flex flex-col gap-2">
              <div
                className="w-[200px] h-[200px] border-4 border-[#FFB800] overflow-hidden"
                style={{ boxShadow: 'inset 0 0 24px rgba(0,0,0,0.8)' }}
              >
                <img
                  src={selected.portraitUrl}
                  alt={selected.name}
                  loading="lazy"
                  referrerPolicy="no-referrer"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="font-doto text-2xl text-white leading-tight">{selected.name}</div>
              <div className="text-xs text-neutral-400">{selected.role}</div>
              <div className="font-mono-tech text-[10px] uppercase tracking-widest text-neutral-500">
                {selected.tenureYears}y @ PG
              </div>
              <div className="flex items-center gap-1 text-[#FFB800] font-mono-tech text-[11px]">
                <FiZap />
                <span>{selected.streakDays}d streak</span>
              </div>
            </div>

            <div className="flex-1 flex flex-col gap-3 min-w-0">
              <div className="flex flex-col gap-1.5">
                {KPI_ROWS.map(({ key, label }) => {
                  const v = selected.kpis[key];
                  const shown = Math.min(v, 100);
                  return (
                    <div key={key} className="flex items-center gap-3">
                      <div className="w-16 font-mono-tech text-[10px] uppercase tracking-widest text-neutral-400">{label}</div>
                      <div className="flex-1 h-2 bg-white/5">
                        <div
                          className="h-full"
                          style={{ width: `${shown}%`, backgroundColor: barColor(v) }}
                        />
                      </div>
                      <div className="w-12 text-right font-doto text-sm text-white">{v}</div>
                    </div>
                  );
                })}
              </div>

              <div className="font-mono-tech text-[10px] uppercase tracking-widest text-neutral-400 border-t border-white/5 pt-2">
                TRAINING: <span className="text-white">{selected.kpis.trainingHrs} HRS</span>
              </div>

              <div className="flex-1 min-h-0">
                <div className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500 mb-2">Badge wall</div>
                {selected.badges.length === 0 ? (
                  <div className="font-mono-tech text-[10px] uppercase tracking-widest text-neutral-500 italic">
                    No badges yet — this person is still earning their stripes.
                  </div>
                ) : (
                  <div className="grid grid-cols-4 gap-2">
                    {selected.badges.map((bid) => {
                      const b = BADGES[bid];
                      if (!b) return null;
                      return (
                        <div
                          key={bid}
                          title={b.desc}
                          className="flex flex-col items-center gap-1"
                        >
                          <div
                            className="w-10 h-10 bg-black flex items-center justify-center border-2"
                            style={{ borderColor: b.color, color: b.color }}
                          >
                            <span className="text-2xl leading-none">{b.glyph}</span>
                          </div>
                          <div className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-400 text-center">
                            {b.short}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </Panel>

        <Panel title="RECENT_ACHIEVEMENTS" subtitle="branch feed" className="col-span-3">
          <div className="h-full overflow-y-auto custom-scrollbar">
            {branchFeed.map((e) => (
              <div key={e.id} className="flex items-start gap-2 px-3 py-2 border-b border-white/5">
                {e.portraitUrl ? (
                  <img
                    src={e.portraitUrl}
                    alt=""
                    loading="lazy"
                    referrerPolicy="no-referrer"
                    className="w-7 h-7 rounded-full object-cover flex-shrink-0"
                  />
                ) : (
                  <div
                    className="w-7 h-7 rounded-full flex-shrink-0 border"
                    style={{ borderColor: e.badgeColor }}
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] text-neutral-300 leading-snug">{e.text}</div>
                  <div className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500 mt-0.5">
                    {formatAgo(e.minutesAgo)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Panel>

        <Panel title="KPI_AVERAGES" subtitle="branch mean per metric" className="col-span-5">
          <div className="h-full p-2">
            <PGBarChart
              categories={['TARGET', 'SLA', 'REPORT', 'FTR', 'CSAT', 'SAFETY', 'ATTEND']}
              series={[
                {
                  name: branch.name,
                  data: kpiAverages,
                  itemStyle: { color: PG.red },
                },
              ]}
              yFormatter={(v) => `${v}%`}
            />
          </div>
        </Panel>

        <Panel title="BADGE_DISTRIBUTION" subtitle="who earned what" className="col-span-4">
          <div className="h-full p-2">
            {badgeDistribution.length === 0 ? (
              <div className="h-full flex items-center justify-center font-mono-tech text-[10px] uppercase tracking-widest text-neutral-500">
                No badges earned yet
              </div>
            ) : (
              <PGPieChart data={badgeDistribution} />
            )}
          </div>
        </Panel>

        <Panel title="COMPARE_TO_PEERS" subtitle={`peer group · ${branch.peerGroup}`} className="col-span-3">
          <div className="h-full overflow-y-auto custom-scrollbar">
            <table className="w-full text-xs">
              <thead className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500 bg-black/40 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2">Branch</th>
                  <th className="text-right px-3 py-2">Score</th>
                  <th className="text-right px-3 py-2">On Tgt</th>
                </tr>
              </thead>
              <tbody>
                {peerRows.map((m) => {
                  const isCurrent = m.id === branchId;
                  return (
                    <tr
                      key={m.id}
                      className={`border-t border-white/5 ${isCurrent ? 'bg-[#D71921]/20' : ''}`}
                    >
                      <td className="px-3 py-2">
                        <Link
                          to={`/sliver/measurability/branch/${m.id}`}
                          className={`text-[11px] ${isCurrent ? 'text-white font-semibold' : 'text-neutral-300 hover:text-white'}`}
                        >
                          {m.name}
                        </Link>
                      </td>
                      <td className="px-3 py-2 text-right font-doto text-base text-white">{m.score}</td>
                      <td className="px-3 py-2 text-right font-mono-tech text-[10px] text-neutral-400">{m.onTargetPct}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>
    </motion.div>
  );
}
